import { useMemo } from 'react';
import type {
  ProcessedPart,
  TodoGroupsData,
  TodoGroup,
  TodoContextData,
} from '~/components/chat/assistant-message/types';
import { isTodoWritePart } from '~/lib/ai';
import type { AssistantMessageTodo } from '~/types/chat';

export function useTodoGroups(processedParts: ProcessedPart[] | null): TodoGroupsData {
  return useMemo(() => {
    if (!processedParts) {
      return { allGroups: [], contextMap: new Map() };
    }

    const contextMap = new Map<string | null, TodoContextData>();
    const allGroups: TodoGroup[] = [];
    const groupsByContext = new Map<string | null, Array<{ index: number; isCompleted: boolean; part: any }>>();

    // find all TodoWrite parts and group by parent context
    processedParts.forEach((part, index) => {
      if (isTodoWritePart(part)) {
        const todos = part.toolInvocation.args.todos as Array<AssistantMessageTodo>;
        const isCompleted = todos.every((todo) => todo.status === 'completed' || todo.status === 'cancelled');
        const parentContext = (part.toolInvocation as any).parentToolUseId || null;

        if (!groupsByContext.has(parentContext)) {
          groupsByContext.set(parentContext, []);
        }

        groupsByContext.get(parentContext)!.push({ index, isCompleted, part });
      }
    });

    // for each context, create groups and context data
    groupsByContext.forEach((todoWrites, parentContext) => {
      const contextGroups: TodoGroup[] = [];
      const firstTodoWriteIndex = todoWrites[0]?.index ?? -1;
      const latestTodoWriteIndex = todoWrites[todoWrites.length - 1]?.index ?? -1;
      const latestTodoWritePart = todoWrites[todoWrites.length - 1]?.part;

      for (let i = 0; i < todoWrites.length; i++) {
        const start = todoWrites[i];

        // find the end of this group (next completed TodoWrite in same context or end of context)
        let endIndex: number | null = null;
        let isActive = true;

        if (start.isCompleted) {
          // this TodoWrite is completed, so the group ends here
          endIndex = start.index;
          isActive = false;
        } else {
          // look for the next completed TodoWrite in the same context
          for (let j = i + 1; j < todoWrites.length; j++) {
            if (todoWrites[j].isCompleted) {
              endIndex = todoWrites[j].index;
              isActive = false;
              break;
            }
          }
        }

        const group: TodoGroup = {
          startIndex: start.index,
          endIndex,
          parentContext,
          isActive,
        };

        contextGroups.push(group);
        allGroups.push(group);

        // if we found a completed group, skip to after it
        if (endIndex !== null && endIndex !== start.index) {
          const completedIndex = todoWrites.findIndex((tw) => tw.index === endIndex);

          if (completedIndex > i) {
            i = completedIndex - 1; // -1 because the for loop will increment
          }
        }
      }

      contextMap.set(parentContext, {
        groups: contextGroups,
        firstTodoWriteIndex,
        latestTodoWriteIndex,
        latestTodoWritePart,
      });
    });

    return { allGroups, contextMap };
  }, [processedParts]);
}
